import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { Observable, zip } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { AlertIdentificators, AlertPayload, AlertResponse, AlertSelectParams } from '@shared/widgets/entities/alerts';
import { WidgetsFacade } from '@shared/widgets/store/widgets.facade';
import { SearchBarState } from '@shared/base-search-bar/store/state/search-bar.state';
import { alertPayloadMapper } from '@shared/widgets/mappers/alert-payload.mapper';
import { invertAlertDateBoundFormat } from '@shared/widgets/mappers/alert-date-format.mapper';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { User } from '@app/modules/core/interfaces/user';
import { BusType } from '@search-bar/entities/bus-type';
import { FlightType } from '@search-bar/entities/flight-type';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { AppFacade } from '@app/app.facade';
import { UserAlertsFacade } from '@user-alerts/store/user-alerts.facade';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { UserAlertsAbstract } from '@user-alerts/abstracts/user-alerts.abstract';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';

@Component({
  selector: 'app-user-alerts-desktop',
  templateUrl: './user-alerts-desktop.component.html',
  styleUrls: ['./user-alerts-desktop.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UserAlertsDesktopComponent extends UserAlertsAbstract {

  @Input()
  public showAlertsManagement: boolean = false;

  @Input()
  public directOnlyParamCreation?: boolean = false;

  @Input()
  public priceParamCreation?: number;

  private _isCreateAlert?: boolean = false;
  private _isDeleteAlertMode?: boolean = false;
  private _isAlertList?: boolean = false;
  public isUpdateAlert: boolean = false;
  private alertIdentificators: AlertIdentificators;

  constructor(
    private widgetsFacade: WidgetsFacade,
    private domManipulateService: DomManipulateService,
    private appFacade: AppFacade,
    private baseSearchBarFacade: BaseSearchBarFacade,
    protected userAlertsFacade: UserAlertsFacade,
    protected amplitude: PropertiesAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService
  ) {
    super(userAlertsFacade, amplitude, triggerAmplitudeService);
  }

  public toggleDesktopAlertsManagement(): void {
    this.showAlertsManagement = !this.showAlertsManagement;
    this.toggleAlertsManagement.emit();
    this.domManipulateService.setBodyScroll(!this.showAlertsManagement);
    this.alertSelected = null;
    this.isUpdateAlert = false;
    this.isCreateAlert = false;
  }

  public confirmDeleteAlertDesktop(alertIdentificators: AlertIdentificators): void {
    this.showConfirmDeleteAlert = !this.showConfirmDeleteAlert;
    this.isDeleteAlertMode = !this.isDeleteAlertMode;
    this.alertIdentificators = alertIdentificators;
  }

  public evaluateDeleteAlert(confirmDeleteAlertAction: boolean): void {

    if (confirmDeleteAlertAction && this.alertIdentificators) {
      this.userAlertsFacade.deleteAlert(this.alertIdentificators);
      this.isDeleteAlertMode = false;
      this.toggleAlertsManagement.emit();
    } else if (!this.isDeleteAlertMode) {
      this.showConfirmDeleteAlert = !this.showConfirmDeleteAlert;
    } else if (confirmDeleteAlertAction) {
      this.isDeleteAlertMode = !this.isDeleteAlertMode;
      this.toggleAlertsManagement.emit();

      this.widgetsFacade.alert$.subscribe(alertSelected => {
        this.alertIdentificators = {
          user_id: alertSelected.user_id,
          id: alertSelected.id
        };
        this.userAlertsFacade.deleteAlert(this.alertIdentificators);
      }).unsubscribe();
    } else {
      this.isDeleteAlertMode = false;
      this.toggleAlertsManagement.emit();
    }

  }

  public updateAlert(alertSelected: AlertResponse): void {
    this.alertSelected = alertSelected;
    this.toggleUpdateAlertForm();
  }

  public toggleUpdateAlertForm(): void {
    if (this.isCreateAlert) {
      this.isCreateAlert = !this.isCreateAlert;
      this.toggleAlertsManagement.emit();
    } else {
      this._isCreateAlert = false;
      this._isDeleteAlertMode = false;
      this._isAlertList = !this._isAlertList;
      this.isUpdateAlert = !this.isUpdateAlert;
    }
  }

  get showCreateUpdateForm(): boolean {
    return this.isCreateAlert || this.isUpdateAlert;
  }

  get maxPriceAlert(): number | string {
    return this.hasAlertSelection ? this.alertSelected?.price_max : this.priceParamCreation;
  }

  get directOnlyAlert(): number | boolean {
    return this.hasAlertSelection ? this.alertSelected?.direct_only : this.directOnlyParamCreation;
  }

  get arrivalNameAlert$(): Observable<string> {
    return this.searchData$.pipe( map( ({arrivalPlace}) => {
      if (this.hasAlertSelection) return this.alertSelected?.arrival_name;
      return arrivalPlace.description;
    }));
  }

  get arrivalDateAlert$(): Observable<Date> {
    return this.searchData$.pipe( map( ({arrivalDate}) => {
      if (this.hasAlertSelection) { 
        return (this.alertSelected ?
          !this.alertSelected.oneway ?
            formatDateByRegex(invertAlertDateBoundFormat(this.alertSelected.inbound_max, !!this.alertSelected.flex_dates, true))
            : null : null);
      }
      return arrivalDate;
    }));
  }

  get departureDateAlert$(): Observable<Date> {
    return this.searchData$.pipe( map( ({departureDate}) => {
      if (this.hasAlertSelection) {
        return  formatDateByRegex(invertAlertDateBoundFormat(this.alertSelected.outbound_min, !!this.alertSelected.flex_dates));
      }
      return departureDate;
    }));
  }

  get searchData$(): Observable<SearchData> { 
    return this.baseSearchBarFacade.searchData$; 
  }

  get flightType$(): Observable<FlightType | BusType> {
    return this.searchData$.pipe(
      map( ({flightType}) => {
        if ( this.hasAlertSelection ) {
          return this.alertSelected.oneway ? FlightType.ONEWAY : FlightType.ROUNDTRIP; 
        }        
        return flightType;
      })
    );
  }

  public createAlertEvent(alertSelectParams: AlertSelectParams): void {
    if (this.hasAlertSelection) {
      this.createAlert(alertSelectParams);
    } else {
      zip(
        this.appFacade.user$.pipe(take(1)),
        this.appFacade.searchData$.pipe(take(1))
      ).subscribe(([user, searchData]) => {
        this.widgetsFacade.createAlert(
          this.getAlertPayload(user, searchData, alertSelectParams.directOnly,
            alertSelectParams.maxPriceAlert, alertSelectParams.flexDates));
      }).unsubscribe();
    }

    this.toggleUpdateAlertForm();
    this.toggleAlertCreated(!this.hasAlertSelection);
    this.alertSelected = null;
  }

  private getAlertPayload(user: User, searchData: SearchBarState, directOnly: boolean,
    maxPriceAlert: string | number, flexDates: boolean): AlertPayload {
    return alertPayloadMapper(user, searchData, directOnly, maxPriceAlert, flexDates);
  }

  public get evaluateManagementMode(): boolean {
    return (this.showAlertsManagement && !this.isDeleteAlertMode)
      || this.isCreateAlert || this.isAlertList || this.isUpdateAlert;
  }

  public get isCreateAlert(): boolean {
    return this._isCreateAlert;
  }

  @Input()
  public set isCreateAlert(value: boolean) {
    this._isCreateAlert = value;
    this._isDeleteAlertMode = false;
    this._isAlertList = false;
  }

  public get isDeleteAlertMode(): boolean {
    if (!this._isDeleteAlertMode) {
      this.alertIdentificators = null;
    }
    return this._isDeleteAlertMode;
  }

  @Input()
  public set isDeleteAlertMode(value: boolean) {
    this._isDeleteAlertMode = value;
    this._isAlertList = false;
    this._isCreateAlert = false;
  }

  public get isAlertList(): boolean {
    return this._isAlertList;
  }

  @Input()
  public set isAlertList(value: boolean) {
    this._isAlertList = value;
    this._isDeleteAlertMode = false;
    this._isCreateAlert = false;
  }

}
